import type { Ref } from 'react';
import { formsContent } from '../../content/forms';

/**
 * The summary shown above a form that failed to submit (spec section 18,
 * "Validation errors are ... summarized when helpful").
 *
 * How it behaves, and why:
 *
 * - The form moves focus here after a failed submit. That announces the whole
 *   summary to a screen reader and puts a sighted keyboard user at the top of
 *   the problem, rather than leaving both to discover a red border somewhere
 *   below the fold.
 * - No `role="alert"`. Focusing a container that is also a live region gets it
 *   announced twice on some screen readers; focus alone is enough and is what
 *   the field-level `aria-describedby` messages back up.
 * - Each entry is a real `<button>` that focuses its field. An anchor to an
 *   input scrolls to it without focusing it, which is the wrong half of the
 *   job.
 *
 * Form-level messages (a dropped connection, a server fault) render in the same
 * box, because "what went wrong" should be in one place regardless of whether
 * the cause was a field or the network.
 */
export interface SummaryFieldError {
  /** DOM id of the control to focus. */
  targetId: string;
  /** The field's visible label, so the entry reads as the field's name. */
  label: string;
  message: string;
}

export interface FormErrorSummaryProps {
  id: string;
  ref?: Ref<HTMLDivElement>;
  fieldErrors: readonly SummaryFieldError[];
  formErrors: readonly string[];
  /**
   * Heading of the box. Defaults to the signup wording; the referral form
   * passes its own, because "that did not send yet" on a page that already
   * says "you're in" needs to name what did not send.
   */
  title?: string;
}

export function FormErrorSummary({
  id,
  ref,
  fieldErrors,
  formErrors,
  title = formsContent.common.errorSummaryTitle,
}: FormErrorSummaryProps) {
  if (fieldErrors.length === 0 && formErrors.length === 0) return null;

  const headingId = `${id}-heading`;

  return (
    <div
      id={id}
      ref={ref}
      tabIndex={-1}
      aria-labelledby={headingId}
      className="mb-8 rounded-md border-2 border-signal bg-field p-4 focus:outline-2 focus:outline-offset-2 focus:outline-signal sm:p-5"
    >
      <p id={headingId} className="text-base font-semibold text-signal">
        {title}
      </p>

      {formErrors.map((message) => (
        <p key={message} className="mt-2 text-sm leading-relaxed text-ink">
          {message}
        </p>
      ))}

      {fieldErrors.length > 0 ? (
        <>
          <p className="mt-2 text-sm leading-relaxed text-ink">
            {formsContent.common.errorSummaryIntro}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink">
            {fieldErrors.map((entry) => (
              <li key={entry.targetId}>
                <button
                  type="button"
                  onClick={() => document.getElementById(entry.targetId)?.focus()}
                  className="text-left font-semibold text-signal underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal"
                >
                  {entry.label}
                </button>
                <span> — {entry.message}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
